import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { surveyPublicApi } from '../../services/surveyApi';
import { SurveyAccessData, SurveyResponseItem } from '../../types/survey';
import { useLanguage } from '../../contexts/LanguageContext';

const SurveyPublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('edit') === 'true';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [surveyData, setSurveyData] = useState<SurveyAccessData | null>(null);
  const [responses, setResponses] = useState<SurveyResponseItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const { t } = useLanguage();

  // AI Assist state
  const [aiLoading, setAiLoading] = useState<number | null>(null);
  const [aiLoadingAll, setAiLoadingAll] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<{[key: number]: Array<{type: string; title: string; content: string}>}>({});
  const [expandedAi, setExpandedAi] = useState<number | null>(null);

  // Password states
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordVerified, setPasswordVerified] = useState(false);

  useEffect(() => {
    if (!token) return;
    loadSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      const data = await surveyPublicApi.getSurveyByToken(token!);

      // Check if password is required
      if ((data as any).hasPassword && !passwordVerified) {
        setNeedsPassword(true);
        setSurveyData(data);
        setLoading(false);
        return;
      }

      setSurveyData(data);

      // Initialize responses from existing or from template
      if (data.existingResponse) {
        setResponses(data.existingResponse.responses);
      } else {
        setResponses(data.questions.map(q => ({
          no: q.no,
          category: q.category,
          question: q.question,
          defaultAnswer: q.defaultAnswer,
          answer: '',
          notes: ''
        })));
      }

      if (data.categories.length > 0) {
        setActiveCategory(data.categories[0]);
      }
    } catch (err: any) {
      const code = err.response?.data?.error?.code;
      if (code === 'TOKEN_EXPIRED' || code === 'SURVEY_CLOSED') {
        navigate(`/survey/${token}/expired`);
      } else {
        setError(err.response?.data?.error?.message || t('surveyPublic', 'loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPassword = async () => {
    if (!token || !passwordInput.trim()) return;
    try {
      const API_URL = window.location.protocol === 'https:' ? '/api' : `http://${window.location.hostname}:3010/api`;
      const res = await fetch(`${API_URL}/survey-access/${token}/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput.trim() })
      });
      if (res.ok) {
        setPasswordVerified(true);
        setNeedsPassword(false);
        setPasswordError('');
        // Reload survey data now that password is verified
        if (surveyData) {
          if (surveyData.existingResponse) {
            setResponses(surveyData.existingResponse.responses);
          } else {
            setResponses(surveyData.questions.map(q => ({
              no: q.no,
              category: q.category,
              question: q.question,
              defaultAnswer: q.defaultAnswer,
              answer: '',
              notes: ''
            })));
          }
          if (surveyData.categories.length > 0) {
            setActiveCategory(surveyData.categories[0]);
          }
        }
      } else {
        setPasswordError('パスワードが正しくありません');
      }
    } catch (err) {
      setPasswordError('認証に失敗しました');
    }
  };

  const handleAnswerChange = useCallback((no: number, field: 'answer' | 'notes', value: string) => {
    setResponses(prev => prev.map(r => r.no === no ? { ...r, [field]: value } : r));

    // Auto-save after 3 seconds of inactivity
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      autoSave();
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAiAssist = async (resp: SurveyResponseItem) => {
    if (!token || !surveyData?.aiEnabled) return;
    setAiLoading(resp.no);
    try {
      const result = await surveyPublicApi.getAiAssist(token, {
        question: resp.question,
        category: resp.category,
        currentAnswer: resp.answer,
        defaultAnswer: resp.defaultAnswer
      });
      setAiSuggestions(prev => ({ ...prev, [resp.no]: result.suggestions }));
      setExpandedAi(resp.no);
    } catch (err) {
      console.error('AI assist failed:', err);
      alert('AIアシストの取得に失敗しました');
    } finally {
      setAiLoading(null);
    }
  };

  const applySuggestion = (no: number, content: string) => {
    const resp = responses.find(r => r.no === no);
    if (resp) {
      const newAnswer = resp.answer ? `${resp.answer}\n\n${content}` : content;
      handleAnswerChange(no, 'answer', newAnswer);
    }
    setExpandedAi(null);
  };

  // AI Assist for all questions in current category
  const handleAiAssistAll = async (questionsToProcess: SurveyResponseItem[]) => {
    if (!token || !surveyData?.aiEnabled) return;

    const questionsToAssist = questionsToProcess.filter(r => !aiSuggestions[r.no]);
    if (questionsToAssist.length === 0) {
      // All questions already have suggestions, just expand them
      setExpandedAi(questionsToProcess[0]?.no || null);
      return;
    }

    setAiLoadingAll(true);

    try {
      // Call AI assist for each question sequentially to avoid overwhelming the server
      for (const resp of questionsToAssist) {
        try {
          const result = await surveyPublicApi.getAiAssist(token, {
            question: resp.question,
            category: resp.category,
            currentAnswer: resp.answer,
            defaultAnswer: resp.defaultAnswer
          });
          setAiSuggestions(prev => ({ ...prev, [resp.no]: result.suggestions }));
        } catch (err) {
          console.error(`AI assist failed for question ${resp.no}:`, err);
          // Continue with other questions even if one fails
        }
      }
      // Expand the first question's suggestions
      if (questionsToProcess.length > 0) {
        setExpandedAi(questionsToProcess[0].no);
      }
    } catch (err) {
      console.error('AI assist all failed:', err);
      alert('AIアシストの取得に失敗しました');
    } finally {
      setAiLoadingAll(false);
    }
  };

  const autoSave = async () => {
    if (!token) return;
    try {
      setSaving(true);
      await surveyPublicApi.saveResponses(token, responses);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!token) return;
    try {
      setSaving(true);
      await surveyPublicApi.saveResponses(token, responses);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err: any) {
      alert(t('surveyPublic', 'saveFailed') + (err.response?.data?.error?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!token) return;
    const unanswered = responses.filter(r => !r.answer || !r.answer.trim()).length;
    if (unanswered > 0) {
      if (!window.confirm(`${t('surveyPublic', 'unansweredPrefix')}${unanswered}${t('surveyPublic', 'unansweredConfirm')}`)) {
        return;
      }
    }

    try {
      setSubmitting(true);
      await surveyPublicApi.submitResponses(token, responses);
      if (isEditMode) {
        alert('回答を更新しました。このページを閉じてください。');
      } else {
        navigate(`/survey/${token}/complete`);
      }
    } catch (err: any) {
      alert(t('surveyPublic', 'submitFailed') + (err.response?.data?.error?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', color: '#666' }}>{t('common', 'loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h2 style={{ color: '#dc3545' }}>{t('common', 'error')}</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', maxWidth: '400px', width: '90%' }}>
          <h2 style={{ marginBottom: '8px' }}>{surveyData?.title || 'アンケート'}</h2>
          <p style={{ color: '#666', marginBottom: '24px' }}>このアンケートにはパスワードが必要です</p>
          <input
            type="password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyPassword(); }}
            placeholder="パスワードを入力"
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem', marginBottom: '12px', boxSizing: 'border-box' }}
          />
          {passwordError && <p style={{ color: '#dc3545', fontSize: '0.85rem', marginBottom: '12px' }}>{passwordError}</p>}
          <button
            onClick={handleVerifyPassword}
            style={{ width: '100%', padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer' }}
          >
            認証
          </button>
        </div>
      </div>
    );
  }

  if (!surveyData) return null;

  const answeredCount = responses.filter(r => r.answer && r.answer.trim()).length;
  const completionRate = Math.round((answeredCount / responses.length) * 100);

  const filteredResponses = activeCategory
    ? responses.filter(r => r.category === activeCategory)
    : responses;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Edit mode banner */}
      {isEditMode && (
        <div style={{ backgroundColor: '#d69e2e', color: 'white', padding: '8px 20px', textAlign: 'center', fontSize: '0.9rem', fontWeight: 600 }}>
          ✏️ 編集モード — 既存の回答を修正できます。修正後「送信」ボタンで更新してください。
        </div>
      )}

      {/* Header */}
      <header style={{ backgroundColor: '#2c5282', padding: '20px', color: 'white', position: 'sticky', top: isEditMode ? '36px' : 0, zIndex: 100 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ margin: 0, fontSize: '1.3rem' }}>{surveyData.title}</h1>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
            <span style={{ fontSize: '0.9rem', opacity: 0.9 }}>
              {t('surveyPublic', 'respondent')}{surveyData.respondentName}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <span style={{ fontSize: '0.85rem' }}>
                {t('surveyPublic', 'progress')}{answeredCount}/{responses.length} ({completionRate}%)
              </span>
              {saving && <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>{t('common', 'saving')}</span>}
              {lastSaved && !saving && <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>{t('surveyPublic', 'lastSaved')}{lastSaved}</span>}
            </div>
          </div>
          {/* Progress bar */}
          <div style={{ marginTop: '8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', height: '6px' }}>
            <div style={{ width: `${completionRate}%`, backgroundColor: '#68d391', height: '100%', borderRadius: '4px', transition: 'width 0.3s' }} />
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', display: 'flex', gap: '20px' }}>
        {/* Category sidebar */}
        {surveyData.categories.length > 1 && (
          <nav style={{ width: '200px', flexShrink: 0 }}>
            <div style={{ position: 'sticky', top: '120px', backgroundColor: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: '0.9rem', color: '#666' }}>{t('surveyPublic', 'category')}</h3>
              {surveyData.categories.map(cat => {
                const catResponses = responses.filter(r => r.category === cat);
                const catAnswered = catResponses.filter(r => r.answer && r.answer.trim()).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      margin: '4px 0',
                      border: 'none',
                      borderRadius: '4px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      backgroundColor: activeCategory === cat ? '#ebf8ff' : 'transparent',
                      color: activeCategory === cat ? '#2c5282' : '#4a5568',
                      fontWeight: activeCategory === cat ? 600 : 400
                    }}
                  >
                    {cat}
                    <span style={{ float: 'right', fontSize: '0.75rem', color: '#999' }}>
                      {catAnswered}/{catResponses.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
        )}

        {/* Questions area */}
        <div style={{ flex: 1 }}>
          {/* AI Assist All Button */}
          {surveyData.aiEnabled && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              padding: '12px 16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.9rem', color: '#4a5568' }}>
                  {activeCategory || '全カテゴリ'} - {filteredResponses.length}問
                </span>
                {Object.keys(aiSuggestions).filter(no => filteredResponses.some(r => r.no === parseInt(no))).length > 0 && (
                  <span style={{
                    fontSize: '0.75rem',
                    padding: '2px 8px',
                    backgroundColor: '#c6f6d5',
                    color: '#276749',
                    borderRadius: '10px'
                  }}>
                    {Object.keys(aiSuggestions).filter(no => filteredResponses.some(r => r.no === parseInt(no))).length}問にAI提案あり
                  </span>
                )}
              </div>
              <button
                onClick={() => handleAiAssistAll(filteredResponses)}
                disabled={aiLoadingAll}
                style={{
                  padding: '10px 20px',
                  backgroundColor: aiLoadingAll ? '#a0aec0' : '#6b46c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: aiLoadingAll ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(107, 70, 193, 0.3)'
                }}
              >
                {aiLoadingAll ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    AI分析中... ({Object.keys(aiSuggestions).filter(no => filteredResponses.some(r => r.no === parseInt(no))).length}/{filteredResponses.length})
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                    全てにAIアシスト
                  </>
                )}
              </button>
            </div>
          )}
          {filteredResponses.map((resp) => (
            <div key={resp.no} style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              borderLeft: resp.answer && resp.answer.trim() ? '4px solid #68d391' : '4px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.8rem', color: '#718096', fontWeight: 600 }}>
                  Q{resp.no}. {resp.category}
                </span>
              </div>
              <p style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 500, color: '#2d3748' }}>
                {resp.question}
              </p>
              {resp.defaultAnswer && (
                <div style={{ margin: '0 0 12px', padding: '8px 12px', backgroundColor: '#f7fafc', borderRadius: '4px', fontSize: '0.85rem', color: '#718096' }}>
                  {t('surveyPublic', 'defaultAnswer')}{resp.defaultAnswer}
                </div>
              )}
              <textarea
                value={resp.answer}
                onChange={(e) => handleAnswerChange(resp.no, 'answer', e.target.value)}
                placeholder={t('surveyPublic', 'answerPlaceholder')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              {/* AI Assist Button */}
              {surveyData.aiEnabled && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => handleAiAssist(resp)}
                    disabled={aiLoading === resp.no}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: aiLoading === resp.no ? '#a0aec0' : '#805ad5',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      cursor: aiLoading === resp.no ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {aiLoading === resp.no ? (
                      <>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        AI分析中...
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                        </svg>
                        AIアシスト
                      </>
                    )}
                  </button>
                  {aiSuggestions[resp.no] && (
                    <button
                      onClick={() => setExpandedAi(expandedAi === resp.no ? null : resp.no)}
                      style={{ padding: '6px 10px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', color: '#4a5568' }}
                    >
                      {expandedAi === resp.no ? '提案を閉じる' : '提案を表示'}
                    </button>
                  )}
                </div>
              )}
              {/* AI Suggestions */}
              {expandedAi === resp.no && aiSuggestions[resp.no] && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f7f7ff', borderRadius: '6px', border: '1px solid #e9e9ff' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#553c9a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                    AIからの提案
                  </h4>
                  {aiSuggestions[resp.no].map((suggestion, idx) => {
                    // Different styling based on suggestion type
                    const typeStyles: { [key: string]: { bg: string; border: string; icon: string; label: string } } = {
                      example: { bg: '#f0fff4', border: '#9ae6b4', icon: '💡', label: '回答例' },
                      scenario: { bg: '#ebf8ff', border: '#90cdf4', icon: '📋', label: 'シナリオ' },
                      togaf: { bg: '#faf5ff', border: '#d6bcfa', icon: '🏛️', label: 'TOGAF ADM' },
                    };
                    const style = typeStyles[suggestion.type] || typeStyles.example;

                    return (
                      <div key={idx} style={{
                        marginBottom: '10px',
                        padding: '12px',
                        backgroundColor: style.bg,
                        borderRadius: '6px',
                        border: `1px solid ${style.border}`,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '1rem' }}>{style.icon}</span>
                            <span style={{
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              color: suggestion.type === 'togaf' ? '#553c9a' : suggestion.type === 'scenario' ? '#2c5282' : '#276749'
                            }}>
                              {suggestion.title}
                            </span>
                            <span style={{
                              fontSize: '0.65rem',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              backgroundColor: suggestion.type === 'togaf' ? '#805ad5' : suggestion.type === 'scenario' ? '#3182ce' : '#38a169',
                              color: 'white',
                            }}>
                              {style.label}
                            </span>
                          </div>
                          <button
                            onClick={() => applySuggestion(resp.no, suggestion.content)}
                            style={{
                              padding: '4px 10px',
                              backgroundColor: '#805ad5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              fontWeight: 500,
                            }}
                          >
                            適用
                          </button>
                        </div>
                        <p style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          color: '#2d3748',
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap',
                        }}>
                          {suggestion.content}
                        </p>
                      </div>
                    );
                  })}
                  {/* TOGAF ADM Reference Link */}
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <span>ℹ️</span>
                    <span>提案はTOGAF ADM（Architecture Development Method）の観点を含んでいます。各フェーズに応じた回答を参考にしてください。</span>
                  </div>
                </div>
              )}
              <input
                type="text"
                value={resp.notes}
                onChange={(e) => handleAnswerChange(resp.no, 'notes', e.target.value)}
                placeholder={t('surveyPublic', 'notesPlaceholder')}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  marginTop: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          ))}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 0', borderTop: '1px solid #e2e8f0', marginTop: '20px' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '12px 24px',
                backgroundColor: '#718096',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? t('common', 'saving') : t('surveyPublic', 'tempSave')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '12px 32px',
                backgroundColor: '#2c5282',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? t('surveyPublic', 'submitting') : isEditMode ? '回答を更新する' : t('surveyPublic', 'submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyPublicPage;
